import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import { GoogleTagManagerService } from '../../../../../common/services/analytics/google-tag-manager.service';
import {
  CdwConfirmationDialogComponent,
  CdwConfirmationDialogData,
} from '@cdw-ae/common-ui-components-dialog';
import { CdwSearchGridStateService } from '@cdw-ae/common-ui-components-search-grid';
import {
  bulkJobEnqueued,
  JobTypes,
  selectIsFullAssetSyncRunning,
} from '@cdw-ae/jobs-status-state';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, Subject, take, takeUntil } from 'rxjs';
import { selectCustomerId } from '../../../../../+state/user-identity/user-identity-settings.selectors';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { AllCustomerAssetsDataService } from '../../../../../common/services/all-customer-assets/all-customer-assets-data.service';
import { DevicesDataService } from '../../../../../common/services/device/devices-data.service';
import { JobMonitorService } from '../../../../../common/services/job-monitor/job-monitor.service';
import {
  getBulkEditConfigForAction,
  isDeviceAction,
} from '../../device-search/configs/bulk-edit-config';
import { MoveToLastUserOuDialogComponent } from '../move-to-last-user-ou-dialog/move-to-last-user-ou-dialog.component';
import {
  REFRESH_CACHE_BUTTON_APPEARANCE,
  REFRESH_CACHE_OPTIONS_BASE,
} from '../refresh-cache-button.config';
import { NavbarActionButtonService } from './navbar-action-button.service';

/**
 * Service that registers navbar buttons for the application.
 * This centralizes button registration to keep navbar component clean and maintainable.
 * Individual components can also register their own buttons using NavbarActionButtonService.
 */
@Injectable({
  providedIn: 'root',
})
export class NavbarButtonsRegistryService {
  private destroy$ = new Subject<void>();
  private customerId = '';
  /** Cache refresh button disabled state - set immediately on click, managed by NgRx thereafter */
  private isRefreshDisabled = false;

  constructor(
    private allCustomerAssetsDataService: AllCustomerAssetsDataService,
    private devicesDataService: DevicesDataService,
    private deviceGridStateService: CdwSearchGridStateService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private jobMonitorService: JobMonitorService,
    private store: Store,
    private router: Router,
    private gtmService: GoogleTagManagerService
  ) {
    // Subscribe to customer ID for job monitoring
    this.store
      .select(selectCustomerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((customerId) => {
        this.customerId = customerId;
      });

    // Subscribe to cache refresh status from NgRx (updates button state)
    this.store
      .select(selectIsFullAssetSyncRunning)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isRunning) => {
        this.isRefreshDisabled = isRunning;
      });

    // Re-register buttons on route change to update tooltips and disabled states
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.registerButtons();
      });

    // Re-register buttons whenever selection count crosses 0 ↔ non-0.
    // The disabled state of "Refresh Selected" + the Actions dropdown
    // items is computed at registration time (the registry doesn't
    // support reactive option-level disabled). We watch selectedCount$
    // and re-register on any cross-zero transition so the buttons
    // light up the moment selection appears and grey out when cleared.
    // Same-side transitions (e.g., 2 → 5) don't need a re-register
    // because the disabled state doesn't change.
    this.deviceGridStateService.selectedCount$
      .pipe(
        takeUntil(this.destroy$),
        map((count) => count > 0),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.updateDeviceActionsDisabledState();
      });
  }

  private navbarButtons = inject(NavbarActionButtonService);

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Monitor a bulk action job via Firestore and show completion/error messages
   *
   * SCOPE: Selection-based bulk actions only (navbar actions monitoring)
   * - Used for: Profile wipe, Factory reset, Move to last user OU
   * - Clears device selection after job starts
   * - Clears updating row state on job completion or failure
   *
   * @param jobId - Job ID to monitor
   * @param deviceIds - Device IDs affected (for updating state cleanup and count display)
   * @param actionName - Human-readable action name (e.g., "Profile wipe", "Factory reset")
   * @param clearSelection - Whether to clear device selection immediately (default: true)
   */
  private monitorBulkActionJob(
    jobId: number,
    deviceIds: string[],
    actionName: string,
    clearSelection = true
  ): void {
    if (clearSelection) {
      this.deviceGridStateService.setSelectedIds([]);
      this.deviceGridStateService.setSelectedCount(0);
    }

    const customerId = parseInt(this.customerId, 10);
    this.jobMonitorService
      .monitorWithCallbacks(
        customerId,
        jobId,
        () => {
          // Success callback — update rows from cache (cache already
          // has fresh data after bulk action). For SSRM dispatches
          // (Refresh Selected, Move-to-last-user-OU) we don't have a
          // client-side id list — `updateRows([])` is a no-op which
          // leaves stale row data on screen. Fall through to the
          // consumer-registered full grid refresh in that case
          // (matches the bulk-edit drawer's `purgeAndRefresh` pattern).
          // Toast notification is handled globally by jobCompletedToast$
          // effect.
          if (deviceIds.length > 0) {
            this.deviceGridStateService.updateRows(deviceIds);
          } else {
            this.requestFullGridRefresh?.();
          }
        },
        () => {
          // Error callback — clear updating state
          // Toast notification is handled globally by jobCompletedToast$ effect
          this.deviceGridStateService.clearUpdating(deviceIds);
        }
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  // Callback for audit log creation (set by device-search component)
  public onDeviceActionJobCreated:
    | ((params: {
        jobId: number;
        deviceIds: string[];
        command: string;
      }) => void)
    | null = null;

  // Callback to get/set cache refreshing state (set by internal-layout)
  // Call with no args to get state, call with boolean to set state
  public isRefreshingCache: ((value?: boolean) => boolean) | null = null;

  // Callback to open export dialog (set by device-search component)
  public openExportDialog: (() => void) | null = null;

  // Callback to open import dialog (set by device-search component)
  public openImportDialog: (() => void) | null = null;

  // Callback to open export-for-bulk-edit dialog (set by device-search component)
  public openExportForBulkEditDialog: (() => void) | null = null;

  // Callback to open bulk edit drawer (set by device-search component)
  public openBulkEditDrawer:
    | ((actionConfig?: {
        visibleFields?: string[];
        defaultValues?: Array<{
          fieldName: string;
          updateType: string;
          value: unknown;
        }>;
        actionType?: string;
      }) => void)
    | null = null;

  /**
   * predicate the device-search consumer registers to signal
   * "an in-cell-edit save is currently in flight". When true, the
   * device-action navbar buttons gate disabled (in addition to their
   * normal hasSelection check) so the user can't dispatch a bulk action
   * mid-save and risk overlapping field updates. Cleared by the
   * consumer when the save's job-started status fires (typically 1-3s).
   * Re-evaluation triggered by the consumer calling
   * `updateDeviceActionsDisabledState()` when `isSaving` toggles.
   */
  public isInCellSaveInFlight: (() => boolean) | null = null;

  /**
   * Callback the device-search consumer registers to trigger a full
   * grid refresh (purgeAndRefresh — clears all loaded blocks and
   * refetches from page 0). Used by post-job-completion handlers when
   * we don't have a per-row id list to do targeted updateRows.
   *
   * In the SSRM model the per-row id list lives in Redis on the
   * server, not the client — so monitorBulkActionJob's
   * `updateRows(deviceIds)` becomes a no-op. The bulk-edit drawer
   * works around this by calling `purgeAndRefresh` directly via its
   * grid @ViewChild reference; the navbar registry is global and
   * doesn't have that reference, hence this callback slot. Mirrors the
   * existing `openExportDialog` / `openBulkEditDrawer` callback
   * pattern.
   */
  public requestFullGridRefresh: (() => void) | null = null;

  /**
   * EDUS-2486 Hit-list #6b — read the current grid session UUID for
   * selection-aware dispatches (Refresh Selected,
   * Move-to-last-user-OU). The UUID is minted by CdwSsrmGridComponent
   * and persisted to sessionStorage at `grid-session:device-search`.
   * Reading directly from sessionStorage matches the pattern Phase 10's
   * BULK_ACTION_EXECUTOR factory uses — no callback wiring required.
   * Returns '' if missing; callers treat empty as "session not ready
   * yet, bail with snackbar".
   */
  private readGridSessionId(): string {
    try {
      return sessionStorage.getItem('grid-session:device-search') ?? '';
    } catch {
      return '';
    }
  }

  /**
   * Register all navbar buttons that should appear across the application.
   * Call this once during app initialization.
   */
  public registerButtons(): void {
    this.registerRefreshCacheButton();
    this.registerDeviceActionsButton();
  }

  /**
   * Update button dropdown options based on current selection state.
   * Call this when device selection changes to update disabled states.
   */
  public updateDeviceActionsDisabledState(): void {
    this.registerRefreshCacheButton();
    this.registerDeviceActionsButton();
  }

  /**
   * Register the refresh cache dropdown for deviceSearch, dashboard, and reports.
   * Two options:
   * - Refresh All: Triggers full-asset-sync-workflow (devices, users, and groupings in parallel)
   * - Refresh Selected: Triggers device-batch-get-workflow for checked devices only (deviceSearch only)
   *
   * Button state uses hybrid approach:
   * - Immediate: Disabled on click to prevent double-clicks
   * - Persistent: NgRx (selectIsFullAssetSyncRunning) handles state across page refreshes
   * - Failsafe: Re-enables on HTTP error, stays disabled when NgRx picks up the job
   */
  private registerRefreshCacheButton(): void {
    // SSRM model: selection lives in Redis on the server; the client
    // only mirrors the count via getSelectedCount(). The legacy
    // getSelectedIds() returns [] in this model — checking its length
    // would always disable the buttons.
    const hasSelection = () => this.deviceGridStateService.getSelectedCount() > 0;

    // Check if we're on device search page
    const isDeviceSearchPage = this.router.url.includes('/deviceSearch');

    // Evaluate selection state at registration time
    const hasCurrentSelection = hasSelection();

    // also gate on in-cell-edit save in flight so users can't
    // dispatch a refresh-selected job while an in-cell save is mid-
    // dispatch (would risk overlapping field updates and confused UX).
    const isSaveInFlight = this.isInCellSaveInFlight?.() ?? false;

    // Disable "Refresh Selected" if not on device search page OR no
    // selection OR in-cell save is currently in flight.
    const refreshSelectedDisabled =
      !(isDeviceSearchPage && hasCurrentSelection) || isSaveInFlight;
    const refreshSelectedTooltip = isDeviceSearchPage
      ? 'Select at least one device from the table to enable this action'
      : 'Only available on Device Search page';

    const options = [
      {
        ...REFRESH_CACHE_OPTIONS_BASE[0],
        disabled: false,
      },
      {
        ...REFRESH_CACHE_OPTIONS_BASE[1],
        disabled: refreshSelectedDisabled,
        ...(refreshSelectedDisabled && { tooltip: refreshSelectedTooltip }),
      },
    ];

    this.navbarButtons.registerButton({
      ...REFRESH_CACHE_BUTTON_APPEARANCE,
      id: 'refresh-cache',
      visibleOnRoutes: ['deviceSearch', 'dashboard', 'reports'],
      order: 1,
      isDisabled: () => this.isRefreshDisabled,
      options,
      onOptionSelected: (option) => {
        if (option.value === 'refresh-all') {
          this.handleRefreshAll();
        } else if (option.value === 'refresh-selected') {
          // EDUS-2486 Hit-list #6b: selection lives in Redis on the
          // server now — dispatch using count for the dialog copy and
          // gridSessionId for the backend to materialize the actual ids.
          this.handleRefreshSelected(
            this.deviceGridStateService.getSelectedCount()
          );
        }
      },
    });
  }

  /**
   * Handle "Refresh All" - shows confirmation dialog then triggers full cache refresh
   */
  private handleRefreshAll(): void {
    const dialogData: CdwConfirmationDialogData = {
      title: 'Refresh All (Entire Fleet)',
      message: '',
      htmlMessage:
        '<strong>WARNING!</strong><br>A full cache refresh may take several hours or longer depending on amount of devices or information being processed. Do you wish to continue?',
      type: 'warning',
      size: 'medium',
      layout: 'warning-box',
      fontFamily: 'Roboto',
      confirmText: 'Continue',
      cancelText: 'Cancel',
      confirmButtonColor: 'primary',
      showCancelButton: true,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: { confirmed: boolean }) => {
      if (result?.confirmed) {
        // Track refresh all for product analytics
        this.gtmService.pushUserAction({
          category: 'refresh_cache',
          action: 'refresh_initiated',
          metadata: { refresh_type: 'all' },
        });

        // Disable immediately to prevent double-clicks
        this.isRefreshDisabled = true;

        this.allCustomerAssetsDataService
          .refreshCache()
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              if (!response.success) {
                // Failed to start - re-enable button
                this.isRefreshDisabled = false;
                this.snackbarService.showError(
                  'Failed to start cache refresh. Please try again.'
                );
              }
              // Success - NgRx will keep button disabled when job appears in Firestore
            },
            error: () => {
              // Error - re-enable button
              this.isRefreshDisabled = false;
              this.snackbarService.showError(
                'Failed to start cache refresh. Please try again.'
              );
            },
          });
      }
    });
  }

  /**
   * Handle "Refresh Selected" — dispatches the device-batch-get-workflow
   * for the user's server-side selection set. EDUS-2486 Hit-list #6b:
   * the backend resolves the affected ids from Redis (via
   * gridSessionId header + viewKey query) instead of the client sending
   * a deviceIds array. Backend caps the resolved set at 500 and rejects
   * larger selections with HTTP 400.
   *
   * Without specific ids on the client, post-completion row marking +
   * targeted cache invalidation aren't possible — the success toast
   * fires globally and the next page-fetch picks up fresh data via the
   * cache invalidation that happens server-side as part of the refresh
   * job's write step.
   */
  private handleRefreshSelected(selectedCount: number): void {
    if (selectedCount <= 0) {
      return;
    }
    const gridSessionId = this.readGridSessionId();
    if (!gridSessionId) {
      this.snackbarService.showError(
        'Grid session not initialized — reload the page and try again.'
      );
      return;
    }

    const dialogData: CdwConfirmationDialogData = {
      title: 'Refresh Selected Devices',
      message: `You have selected ${selectedCount} device${selectedCount === 1 ? '' : 's'} to refresh. Would you like to continue?`,
      type: 'info',
      size: 'small',
      hideIcon: true,
      confirmText: 'Continue',
      cancelText: 'Cancel',
      confirmButtonColor: 'primary',
      showCancelButton: true,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: { confirmed: boolean }) => {
      if (result?.confirmed) {
        // Track refresh selected for product analytics
        this.gtmService.pushUserAction({
          category: 'refresh_cache',
          action: 'refresh_initiated',
          metadata: {
            refresh_type: 'selected',
            device_count: selectedCount,
          },
        });

        // EDUS-2486 Hit-list #6b: no client-side ids → no
        // markAsUpdating / clearUpdating / updateRows by id; the
        // post-job cache-invalidation server-side will trigger fresh
        // data on the next page-fetch.
        //
        // SSRM selection-lock failsafe: selection is NOT cleared on
        // dispatch (clearSelection: false below) so the lock-derived
        // spinners can render on the still-selected rows. The lock is
        // the new "something is happening" signal — the previous
        // unambiguous-state UX (clear selection on dispatch) is
        // replaced by the per-row spinner.
        this.devicesDataService
          .forceRefreshUsingSelection(
            gridSessionId,
            undefined,
            JobTypes.REFRESH_SELECTED
          )
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              if (response.error) {
                this.snackbarService.showError(
                  `Failed to start device refresh: ${response.error}`
                );
                return;
              }
              if (!response.jobId) {
                this.snackbarService.showError(
                  'Failed to start device refresh. Please try again.'
                );
                return;
              }
              const jobId = parseInt(response.jobId, 10);
              if (!Number.isFinite(jobId)) {
                // Backend returned a non-numeric jobId — monitoring would
                // silently hang on a NaN selector. Bail with cleanup.
                this.snackbarService.showError(
                  'Device refresh dispatched but job ID is invalid — check the Jobs panel.'
                );
                return;
              }
              // Optimistic lock activation: dispatch bulkJobEnqueued so
              // the lock UI engages instantly. Without this, the lock
              // would still activate via the Firestore listener once the
              // saas-device-api enrichment-bearing execution doc lands —
              // typically within 1-2s — but the visual delay is
              // jarring during which the user could still attempt to
              // interact. The optimistic dispatch closes that gap.
              this.store.dispatch(
                bulkJobEnqueued({
                  jobId,
                  gridSessionUuid: gridSessionId,
                  selectedCount,
                })
              );
              // Empty deviceIds: monitorBulkActionJob's per-id
              // updateRows / clearUpdating calls become no-ops which
              // is the intended SSRM behavior — page-fetch picks up
              // fresh data via server-side cache invalidation.
              // clearSelection: false — the lock-derived spinner pattern
              // requires the selection to remain visible. Lock release
              // (via Firestore terminal state OR optimistic cancel) is
              // the new "this is done" signal.
              this.monitorBulkActionJob(
                jobId,
                [],
                'Device refresh',
                false
              );
            },
            error: () => {
              this.snackbarService.showError(
                'Failed to start device refresh. Please try again.'
              );
            },
          });
      }
    });
  }

  /**
   * Register the device actions dropdown for deviceSearch
   */
  private registerDeviceActionsButton(): void {
    // SSRM model: selection lives in Redis on the server; the client
    // only mirrors the count via getSelectedCount(). The legacy
    // getSelectedIds() returns [] in this model — checking its length
    // would always disable the buttons.
    const hasSelection = () => this.deviceGridStateService.getSelectedCount() > 0;

    // Evaluate selection state at registration time
    const hasCurrentSelection = hasSelection();
    // also gate on in-cell-edit save in flight. The dropdown's
    // bulk-action options use `disabled: !hasCurrentSelection` below;
    // OR-in the save-in-flight check so they stay disabled while the
    // user's pending in-cell save is mid-dispatch.
    const isSaveInFlight = this.isInCellSaveInFlight?.() ?? false;
    const actionsDisabled = !hasCurrentSelection || isSaveInFlight;

    this.navbarButtons.registerButton({
      id: 'device-actions',
      label: 'Actions',
      level: 'secondary',
      icon: 'electric_bolt',
      iconSize: '24px',
      iconPosition: 'left',
      useMatIcon: true,
      fontColor: TailwindColors.tertiaryBlueCta,
      fontWeight: 700,
      fontSize: '14px',
      fontFamily: 'Roboto',
      borderColor: TailwindColors.tertiaryBlueCta,
      iconColor: TailwindColors.tertiaryBlueCta,
      buttonWidth: '160px',
      panelWidth: '265px',
      panelPosition: 'before',
      mode: 'wide',
      infoText:
        'Some actions may be disabled due to your permissions/OUs. Use Account Profile page to view actionable assigned permissions.',
      infoBackgroundColor: TailwindColors.gray50,
      infoFontColor: TailwindColors.gray400,
      infoFontFamily: 'Roboto',
      infoFontSize: '14px',
      infoFontWeight: '400',
      visibleOnRoutes: ['deviceSearch'],
      order: 2,
      options: [
        // Actions that require device selection
        {
          label: 'Move OU',
          value: 'move-ou',
          disabled: actionsDisabled,
          tooltip: actionsDisabled
            ? 'Select at least one device from the table to enable this action'
            : undefined,
        },
        {
          label: "Move device to last user's OU",
          value: 'move-last-user-ou',
          disabled: actionsDisabled,
          tooltip: actionsDisabled
            ? 'Select at least one device from the table to enable this action'
            : undefined,
        },
        {
          label: 'Edit device info',
          value: 'edit-import',
          disabled: actionsDisabled,
          tooltip: actionsDisabled
            ? 'Select at least one device from the table to enable this action'
            : undefined,
        },
        { label: '', separator: true },
        {
          label: 'Import for Bulk Edit',
          value: 'import-for-bulk-edit',
        },
        {
          label: 'Export for Bulk Edit',
          value: 'export-for-bulk-edit',
        },
        { label: '', separator: true },
        {
          label: 'Disable',
          value: 'disable',
          disabled: actionsDisabled,
          tooltip: actionsDisabled
            ? 'Select at least one device from the table to enable this action'
            : undefined,
        },
        {
          label: 'Enable',
          value: 'enable',
          disabled: actionsDisabled,
          tooltip: actionsDisabled
            ? 'Select at least one device from the table to enable this action'
            : undefined,
        },
        { label: '', separator: true },
        {
          label: 'Profile wipe',
          value: 'profile-wipe',
          disabled: actionsDisabled,
          tooltip: actionsDisabled
            ? 'Select at least one device from the table to enable this action'
            : undefined,
        },
        {
          label: 'Factory reset',
          value: 'factory-reset',
          disabled: actionsDisabled,
          tooltip: actionsDisabled
            ? 'Select at least one device from the table to enable this action'
            : undefined,
        },
        { label: '', separator: true },
        {
          label: 'Deprovision',
          value: 'deprovision',
          disabled: actionsDisabled,
          tooltip: actionsDisabled
            ? 'Select at least one device from the table to enable this action'
            : undefined,
        },
        { label: '', separator: true },
        {
          // Always enabled: opens the export dialog where the user can
          // export all rows without a selection. The dialog itself gates
          // the "Selected rows" scope on whether any rows are selected.
          label: 'Export for Reporting',
          value: 'export-for-reporting',
        },
      ],
      onOptionSelected: (option) => {
        // EDUS-2486 Hit-list #6b: pass count instead of ids — selection
        // lives in Redis on the server, the client only mirrors the
        // count. Handlers that need ids materialize them at dispatch
        // time via useSelection-aware backend endpoints.
        this.handleDeviceAction(
          option.value,
          this.deviceGridStateService.getSelectedCount()
        );
      },
    });
  }

  /**
   * Handle device action selection. Receives the current selection
   * COUNT (not ids) since selection is server-side authoritative in the
   * SSRM model. Handlers that need to dispatch with ids resolve them
   * server-side via useSelection-aware endpoints; the count is enough
   * for dialog copy ("X devices selected").
   */
  private handleDeviceAction(
    action: string | undefined,
    selectedCount: number
  ): void {
    if (!action) {
      return;
    }

    // Check if this action uses the bulk edit drawer (type-safe check).
    // The drawer's BULK_ACTION_EXECUTOR factory (Phase 10) already uses
    // the useSelection path — it doesn't need an ids list either.
    if (isDeviceAction(action)) {
      if (this.openBulkEditDrawer) {
        const actionConfig = getBulkEditConfigForAction(action);
        this.openBulkEditDrawer(actionConfig);
      }
      return;
    }

    // Handle non-bulk-edit actions directly
    switch (action) {
      case 'refresh':
        // Legacy "refresh visible" path — relied on a client-side id
        // list that doesn't exist in the SSRM model. The user-facing
        // Refresh Selected affordance is in the Refresh Cache dropdown
        // and uses forceRefreshUsingSelection. Leaving this branch as
        // a no-op since no current UI path emits action='refresh'.
        break;
      case 'export-for-reporting':
        this.openExportDialog?.();
        break;
      case 'import-for-bulk-edit':
        this.openImportDialog?.();
        break;
      case 'export-for-bulk-edit':
        this.openExportForBulkEditDialog?.();
        break;
      case 'move-last-user-ou':
        if (selectedCount <= 0) {
          return;
        }
        this.showMoveToLastUserOuDialog(selectedCount);
        break;
      default:
      // console.warn(`Unknown device action: ${action}`);
    }
  }

  /**
   * Show dialog for moving devices to last user's OU.
   *
   * EDUS-2486 Hit-list #6b: receives the selection COUNT (not ids).
   * The PREVIEW step uses useSelection so the per-device OU mapping is
   * fetched without sending the id list. The SYNC step also uses
   * useSelection: saas-device-api applies the dialog's skip rules itself
   * so the audit log can count the skipped devices (EDUS-2625).
   */
  private showMoveToLastUserOuDialog(selectedCount: number): void {
    const gridSessionId = this.readGridSessionId();
    if (!gridSessionId) {
      this.snackbarService.showError(
        'Grid session not initialized — reload the page and try again.'
      );
      return;
    }

    // Open the dialog immediately with loading state. deviceIds is empty
    // (we don't have them client-side) but totalDeviceCount carries the
    // user-visible "X devices selected" count.
    const dialogRef = this.dialog.open(MoveToLastUserOuDialogComponent, {
      data: {
        deviceIds: [],
        totalDeviceCount: selectedCount,
        // deviceToOuMapping is undefined, dialog will show loading state
      },
      disableClose: true,
    });

    // Fetch the preview via useSelection so the backend resolves ids
    // from Redis instead of receiving them in the query string.
    this.devicesDataService
      .getDeviceToOuPreviewUsingSelection(gridSessionId)
      .pipe(take(1))
      .subscribe({
        next: (deviceToOuMapping) => {
          // Update the dialog component with the fetched data
          dialogRef.componentInstance.updateWithOuMapping(deviceToOuMapping);
        },
        error: (error) => {
          // Update the dialog component with error state
          dialogRef.componentInstance.updateWithOuMapping(null);
          this.snackbarService.showError(
            `Failed to fetch OU preview: ${error.message || 'Unknown error occurred'}`,
            'Close',
            {
              duration: 10000,
              horizontalPosition: 'center',
            }
          );
        },
      });

    // Handle dialog close
    dialogRef.afterClosed().subscribe((result: { confirmed: boolean; deviceIdsToMove?: string[] }) => {
      if (!result?.confirmed) {
        return;
      }
      // EDUS-2366: dialog returns the filtered subset that actually needs to move
      // (excludes devices already in their target OU, and devices with no resolvable target).
      // Used here only for the row feedback; the backend re-applies the same filter.
      const deviceIdsToMove = result.deviceIdsToMove ?? [];
      if (deviceIdsToMove.length === 0) {
        return;
      }
      this.devicesDataService
        .syncDevicesToLastUserOuUsingSelection(gridSessionId)
        .pipe(take(1))
        .subscribe({
            next: (response) => {
              if (
                response &&
                typeof response === 'object' &&
                'success' in response &&
                'id' in response
              ) {
                const jobId = (response as { success: boolean; id: number }).id;

                // Mark rows as updating for visual feedback
                this.deviceGridStateService.markAsUpdating(deviceIdsToMove);

                this.onDeviceActionJobCreated?.({
                  jobId,
                  deviceIds: deviceIdsToMove,
                  command: 'SYNC_LAST_USER_OU',
                });

                this.snackbarService.showMessage(
                  `Move to last user OU job started for ${deviceIdsToMove.length} device(s).`,
                  'Close',
                  {
                    duration: 5000,
                    horizontalPosition: 'center',
                  }
                );

                // Monitor job completion via Firestore and clear selection
                this.monitorBulkActionJob(
                  jobId,
                  deviceIdsToMove,
                  'Move to last user OU'
                );
              } else {
                this.snackbarService.showError(
                  'Failed to schedule device OU sync. Please try again.',
                  'Close',
                  {
                    duration: 10000,
                    horizontalPosition: 'center',
                  }
                );
              }
            },
            error: (error) => {
              this.snackbarService.showError(
                `Failed to schedule device OU sync: ${error.message || 'Unknown error occurred'}`,
                'Close',
                {
                  duration: 10000,
                  horizontalPosition: 'center',
                }
              );
            },
          });
    });
  }
}
